import React, { useState, useEffect, useCallback, useRef } from "react"
import API from "../../utils/API"
import "./AiTargetingModuleWizard.css"

const STEP_LABELS_ACTIVATE = ["Recherche", "Vérification", "Activation", "Statut", "Configuration"]
const STEP_LABELS_CONNECT = ["Recherche", "Vérification", "Connexion", "Statut", "Configuration"]
const DISCOVERY_POLL_INTERVAL_MS = 2000
const DISCOVERY_TIMEOUT_MS = 10000
const ACTIVATION_POLL_INTERVAL_MS = 2000
const ACTIVATION_TIMEOUT_MS = 30000

export default function AiTargetingModuleWizard({ totemId, isOpen, onClose, onConfigured }) {
  const [step, setStep] = useState(1)
  const [discovering, setDiscovering] = useState(false)
  const [devices, setDevices] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [activating, setActivating] = useState(false)
  const [activationResult, setActivationResult] = useState(null)
  const [connecting, setConnecting] = useState(false)
  const [connectionResult, setConnectionResult] = useState(null)
  const [status, setStatus] = useState(null)
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [deviceInfoLoading, setDeviceInfoLoading] = useState(false)
  const [deviceInfoError, setDeviceInfoError] = useState(null)
  const [error, setError] = useState(null)
  const [configuring, setConfiguring] = useState(false)
  const [configResults, setConfigResults] = useState(null)
  const [configError, setConfigError] = useState(null)

  const isAlreadyActivated = selectedDevice?.active_status === 1

  const pollRef = useRef(null)
  const timeoutRef = useRef(null)
  const activationTriggered = useRef(false)

  const clearTimers = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearTimers()
  }, [clearTimers])

  const fetchStatus = useCallback(
    () =>
      new Promise((resolve) => {
        API.GET(
          "/admin/totem/" + totemId + "/ai-targeting-module/status",
          (response) => resolve(response.data),
          () => resolve(null),
        )
      }),
    [totemId],
  )

  const startDiscovery = useCallback(() => {
    clearTimers()
    setDiscovering(true)
    setError(null)
    setDevices([])
    setSelectedDevice(null)

    API.POST(
      "/admin/totem/" + totemId + "/ai-targeting-module/discover",
      {},
      () => {
        const startTime = Date.now()

        pollRef.current = setInterval(() => {
          fetchStatus().then((config) => {
            if (config?.discoveryDevices?.length > 0) {
              clearTimers()
              setDevices(config.discoveryDevices)
              setDiscovering(false)
              return
            }

            if (Date.now() - startTime >= DISCOVERY_TIMEOUT_MS) {
              clearTimers()
              setDiscovering(false)
              setError(
                "Aucun module IA trouvé sur le réseau. Vérifiez que le module est branché et connecté au même réseau que le totem.",
              )
            }
          })
        }, DISCOVERY_POLL_INTERVAL_MS)
      },
      (errorMessage) => {
        setDiscovering(false)
        setError(errorMessage)
      },
    )
  }, [totemId, fetchStatus, clearTimers])

  const startActivation = useCallback(() => {
    clearTimers()
    setActivating(true)
    setError(null)
    setActivationResult(null)

    const activateBody = { ip: selectedDevice.ip }
    const devicePort = selectedDevice.port || selectedDevice.proto_info?.[0]?.proto_port
    if (devicePort) activateBody.port = devicePort

    API.POST(
      "/admin/totem/" + totemId + "/ai-targeting-module/activate",
      activateBody,
      () => {
        const startTime = Date.now()

        pollRef.current = setInterval(() => {
          fetchStatus().then((config) => {
            if (config?.activationStatus === "activated") {
              clearTimers()
              setActivating(false)
              setActivationResult({ success: true })
              timeoutRef.current = setTimeout(() => setStep(4), 1500)
              return
            }

            if (config?.activationStatus === "error") {
              clearTimers()
              setActivating(false)
              setActivationResult({ success: false })
              setError(config.lastConnectionError || "Erreur lors de l'activation du module IA.")
              return
            }

            if (Date.now() - startTime >= ACTIVATION_TIMEOUT_MS) {
              clearTimers()
              setActivating(false)
              setError("L'activation a expiré. Veuillez réessayer.")
            }
          })
        }, ACTIVATION_POLL_INTERVAL_MS)
      },
      (errorMessage) => {
        setActivating(false)
        setError(errorMessage)
      },
    )
  }, [totemId, selectedDevice, fetchStatus, clearTimers])

  const startConnection = useCallback((username, password) => {
    setConnecting(true)
    setError(null)
    setConnectionResult(null)

    const connectBody = { ip: selectedDevice.ip, username, password }
    const devicePort = selectedDevice.port || selectedDevice.proto_info?.[0]?.proto_port
    if (devicePort) connectBody.port = devicePort

    API.POST(
      "/admin/totem/" + totemId + "/ai-targeting-module/connect",
      connectBody,
      () => {
        setConnecting(false)
        setConnectionResult({ success: true })
        timeoutRef.current = setTimeout(() => setStep(4), 1500)
      },
      (errorMessage) => {
        setConnecting(false)
        setConnectionResult({ success: false })
        setError(errorMessage)
      },
    )
  }, [totemId, selectedDevice]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start discovery on mount
  useEffect(() => {
    startDiscovery()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start activation when entering step 3 (only for non-activated devices)
  useEffect(() => {
    if (step === 3 && !activationTriggered.current && !isAlreadyActivated) {
      activationTriggered.current = true
      startActivation()
    }
    if (step !== 3) {
      activationTriggered.current = false
    }
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDeviceInfo = useCallback(() => {
    setDeviceInfoLoading(true)
    setDeviceInfoError(null)
    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/device-info`,
      {},
      (response) => {
        setDeviceInfo(response.data)
        setDeviceInfoLoading(false)
      },
      (errorMessage) => {
        setDeviceInfoError(errorMessage)
        setDeviceInfoLoading(false)
      },
    )
  }, [totemId])

  useEffect(() => {
    if (step !== 4) return
    fetchStatus().then(setStatus)
    fetchDeviceInfo()
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  const startConfiguration = useCallback(() => {
    setConfiguring(true)
    setConfigResults(null)
    setConfigError(null)

    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/configure`,
      {},
      (response) => {
        setConfigResults(response.data?.results ?? [])
        setConfiguring(false)
      },
      (errorMessage) => {
        setConfigError(errorMessage)
        setConfiguring(false)
      },
    )
  }, [totemId])

  const configurationTriggered = useRef(false)

  useEffect(() => {
    if (step === 5 && !configurationTriggered.current) {
      configurationTriggered.current = true
      startConfiguration()
    }
    if (step !== 5) {
      configurationTriggered.current = false
    }
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.classList.add("modal-open")
    return () => document.body.classList.remove("modal-open")
  }, [])

  const handleClose = () => {
    clearTimers()
    onClose()
  }

  const handleFinish = () => {
    onConfigured()
    handleClose()
  }

  const canGoNext = () => {
    if (step === 1) return selectedDevice != null
    if (step === 2) return true
    return false
  }

  const handleNext = () => {
    if (step < 5) setStep(step + 1)
  }

  const handleBack = () => {
    if (step <= 1) return
    clearTimers()
    setError(null)
    setActivating(false)
    setActivationResult(null)
    setConnecting(false)
    setConnectionResult(null)
    setStep(step - 1)
  }

  if (!isOpen) return null

  return (
    <>
      <div className="modal fade show" style={{ display: "block", position: "fixed", inset: 0, zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header bg-info">
              <h5 className="modal-title text-white">
                <i className="fas fa-microchip mr-2"></i>
                Configuration du module IA
              </h5>
              <button type="button" className="close text-white" onClick={handleClose}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <StepIndicator currentStep={step} labels={isAlreadyActivated ? STEP_LABELS_CONNECT : STEP_LABELS_ACTIVATE} />
              {step === 1 && (
                <StepDiscovery
                  discovering={discovering}
                  devices={devices}
                  selectedDevice={selectedDevice}
                  error={error}
                  onSelectDevice={setSelectedDevice}
                  onRetry={startDiscovery}
                />
              )}
              {step === 2 && <StepReview device={selectedDevice} isAlreadyActivated={isAlreadyActivated} />}
              {step === 3 && !isAlreadyActivated && (
                <StepActivation
                  activating={activating}
                  activationResult={activationResult}
                  error={error}
                  onRetry={startActivation}
                />
              )}
              {step === 3 && isAlreadyActivated && (
                <StepConnect
                  connecting={connecting}
                  connectionResult={connectionResult}
                  error={error}
                  onConnect={startConnection}
                />
              )}
              {step === 4 && (
                <StepMonitor
                  status={status}
                  deviceInfo={deviceInfo}
                  deviceInfoLoading={deviceInfoLoading}
                  deviceInfoError={deviceInfoError}
                  onRefreshDeviceInfo={fetchDeviceInfo}
                />
              )}
              {step === 5 && (
                <StepConfiguration
                  configuring={configuring}
                  configResults={configResults}
                  configError={configError}
                  onRetry={startConfiguration}
                />
              )}
            </div>
            <div className="modal-footer">
              {step > 1 && step < 4 && (
                <button className="btn btn-default" onClick={handleBack}>
                  <i className="fas fa-arrow-left mr-1"></i> Retour
                </button>
              )}
              {step === 1 && devices.length > 0 && !discovering && (
                <button className="btn btn-default" onClick={startDiscovery}>
                  <i className="fas fa-redo mr-1"></i> Relancer
                </button>
              )}
              <div className="ml-auto">
                {step < 4 ? (
                  <>
                    <button className="btn btn-default mr-2" onClick={handleClose}>
                      Annuler
                    </button>
                    {step < 3 && (
                      <button className="btn btn-info" onClick={handleNext} disabled={!canGoNext()}>
                        Continuer <i className="fas fa-arrow-right ml-1"></i>
                      </button>
                    )}
                    {step === 3 && !isAlreadyActivated && !activating && !activationResult?.success && (
                      <button className="btn btn-info" onClick={startActivation}>
                        <i className="fas fa-redo mr-1"></i> Réessayer
                      </button>
                    )}
                  </>
                ) : step === 4 ? (
                  <>
                    <button className="btn btn-default mr-2" onClick={handleClose}>
                      Fermer
                    </button>
                    <button className="btn btn-info" onClick={handleNext}>
                      Suivant <i className="fas fa-arrow-right ml-1"></i>
                    </button>
                  </>
                ) : (
                  <>
                    {!configuring && configResults?.some((r) => !r.success) && (
                      <button className="btn btn-outline-warning mr-2" onClick={startConfiguration}>
                        <i className="fas fa-redo mr-1"></i> Réessayer
                      </button>
                    )}
                    <button className="btn btn-success" onClick={handleFinish}>
                      <i className="fas fa-check mr-1"></i> Terminer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}

function StepIndicator({ currentStep, labels }) {
  return (
    <div className="ai-wizard-steps mb-3">
      {labels.map((label, i) => {
        const stepNum = i + 1
        let className = "ai-wizard-step"
        if (stepNum < currentStep) className += " completed"
        else if (stepNum === currentStep) className += " active"

        return (
          <div key={stepNum} className={className}>
            <div className="ai-wizard-step-number">
              {stepNum < currentStep ? <i className="fas fa-check"></i> : stepNum}
            </div>
            <div className="ai-wizard-step-label">{label}</div>
          </div>
        )
      })}
    </div>
  )
}

function StepDiscovery({ discovering, devices, selectedDevice, error, onSelectDevice, onRetry }) {
  const [manualMode, setManualMode] = useState(false)
  const [manualIp, setManualIp] = useState("")
  const [manualPort, setManualPort] = useState("80")
  const [manualIpError, setManualIpError] = useState(null)

  const IP_REGEX = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/

  const handleManualSubmit = () => {
    const trimmed = manualIp.trim()
    if (!IP_REGEX.test(trimmed)) {
      setManualIpError("Adresse IP invalide.")
      return
    }
    const portNum = parseInt(manualPort, 10)
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      setManualIpError("Port invalide (1-65535).")
      return
    }
    setManualIpError(null)
    onSelectDevice({ ip: trimmed, port: portNum, manual: true })
  }

  if (discovering) {
    return (
      <div className="text-center my-4">
        <i className="fas fa-2x fa-sync-alt fa-spin text-info"></i>
        <p className="mt-2">Recherche du module IA sur le réseau...</p>
      </div>
    )
  }

  return (
    <>
      <p>Recherche du module de ciblage IA connecté au réseau local du totem.</p>

      {devices.length > 0 ? (
        <table className="table table-striped table-sm">
          <thead>
            <tr>
              <th></th>
              <th>Modèle</th>
              <th>IP</th>
              <th>MAC</th>
              <th>Firmware</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device, idx) => (
              <tr
                key={idx}
                className={`ai-wizard-device-row ${selectedDevice === device ? "table-active" : ""}`}
                onClick={() => onSelectDevice(device)}
              >
                <td>
                  <input type="radio" checked={selectedDevice === device} readOnly />
                </td>
                <td>{device.model || "-"}</td>
                <td>{device.ip}</td>
                <td>{device.mac || "-"}</td>
                <td>{device.firmware_version || "-"}</td>
                <td>
                  {device.active_status === 1 ? (
                    <span className="badge badge-success">Activé</span>
                  ) : (
                    <span className="badge badge-warning">Non activé</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        !error && !manualMode && (
          <button className="btn btn-info" onClick={onRetry}>
            <i className="fas fa-search mr-1"></i> Rechercher
          </button>
        )
      )}

      {error && !manualMode && (
        <div className="alert alert-danger mt-3">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {error}
          <div className="mt-2">
            <button className="btn btn-sm btn-outline-danger mr-2" onClick={onRetry}>
              <i className="fas fa-redo mr-1"></i> Réessayer
            </button>
            <button className="btn btn-sm btn-outline-info" onClick={() => setManualMode(true)}>
              <i className="fas fa-keyboard mr-1"></i> Saisir l'adresse IP manuellement
            </button>
          </div>
        </div>
      )}

      {manualMode && (
        <div className="card mt-3">
          <div className="card-body">
            <h6 className="card-title">
              <i className="fas fa-keyboard mr-1"></i>
              Saisie manuelle de l'adresse IP
            </h6>
            <p className="text-muted small">
              Si la découverte automatique ne trouve pas le module, vous pouvez saisir son adresse IP directement.
            </p>
            <div className="form-row align-items-end">
              <div className="col">
                <label className="small font-weight-bold">Adresse IP</label>
                <input
                  type="text"
                  className={`form-control ${manualIpError ? "is-invalid" : ""}`}
                  placeholder="Ex : 192.168.1.100"
                  value={manualIp}
                  onChange={(e) => { setManualIp(e.target.value); setManualIpError(null) }}
                  onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                />
                {manualIpError && <div className="invalid-feedback">{manualIpError}</div>}
              </div>
              <div className="col-3">
                <label className="small font-weight-bold">Port</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="80"
                  value={manualPort}
                  min="1"
                  max="65535"
                  onChange={(e) => setManualPort(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                />
              </div>
              <div className="col-auto">
                <button className="btn btn-info" onClick={handleManualSubmit}>
                  <i className="fas fa-check mr-1"></i> Valider
                </button>
              </div>
            </div>
            <button className="btn btn-sm btn-link text-muted mt-2 p-0" onClick={() => { setManualMode(false); setManualIpError(null) }}>
              <i className="fas fa-arrow-left mr-1"></i> Retour à la découverte automatique
            </button>
          </div>
        </div>
      )}

      {selectedDevice?.manual && (
        <div className="alert alert-info mt-3">
          <i className="fas fa-check-circle mr-1"></i>
          Module configuré manuellement à l'adresse <strong>{selectedDevice.ip}:{selectedDevice.port}</strong>
        </div>
      )}
    </>
  )
}

function StepReview({ device, isAlreadyActivated }) {
  if (!device) return null

  return (
    <>
      <p>Vérifiez les informations du module détecté avant de procéder.</p>

      <table className="table table-bordered table-sm">
        <tbody>
          <tr>
            <th style={{ width: "35%" }}>Modèle</th>
            <td>{device.model || "-"}</td>
          </tr>
          <tr>
            <th>Adresse IP</th>
            <td>{device.ip}</td>
          </tr>
          <tr>
            <th>Adresse MAC</th>
            <td>{device.mac || "-"}</td>
          </tr>
          <tr>
            <th>N° de série</th>
            <td>{device.sn || "-"}</td>
          </tr>
          <tr>
            <th>Firmware</th>
            <td>{device.firmware_version || "-"}</td>
          </tr>
        </tbody>
      </table>

      {isAlreadyActivated ? (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          Ce module est déjà activé. Vous devrez saisir les identifiants existants pour vous y connecter.
        </div>
      ) : (
        <div className="alert alert-info">
          <i className="fas fa-info-circle mr-1"></i>
          Un mot de passe sécurisé sera généré automatiquement pour ce module.
        </div>
      )}
    </>
  )
}

function StepActivation({ activating, activationResult, error, onRetry }) {
  if (activating) {
    return (
      <div className="text-center my-4">
        <i className="fas fa-2x fa-sync-alt fa-spin text-info"></i>
        <p className="mt-2">Activation en cours...</p>
      </div>
    )
  }

  if (activationResult?.success) {
    return (
      <div className="alert alert-success my-4">
        <i className="fas fa-check-circle mr-1"></i>
        Module IA activé avec succès !
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {error}
        </div>
      )}
      {!error && <p>Prêt à activer le module IA.</p>}
    </>
  )
}

function StepConnect({ connecting, connectionResult, error, onConnect }) {
  const [username, setUsername] = useState("admin")
  const [password, setPassword] = useState("")

  if (connecting) {
    return (
      <div className="text-center my-4">
        <i className="fas fa-2x fa-sync-alt fa-spin text-info"></i>
        <p className="mt-2">Connexion au module IA en cours...</p>
      </div>
    )
  }

  if (connectionResult?.success) {
    return (
      <div className="alert alert-success my-4">
        <i className="fas fa-check-circle mr-1"></i>
        Connexion au module IA réussie !
      </div>
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!password.trim()) return
    onConnect(username.trim(), password)
  }

  return (
    <>
      <p>
        Ce module est déjà activé. Veuillez saisir les identifiants pour vous y connecter.
      </p>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="font-weight-bold">Nom d'utilisateur</label>
          <input
            type="text"
            className="form-control"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="font-weight-bold">Mot de passe</label>
          <input
            type="password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            placeholder="Mot de passe du module IA"
          />
        </div>
        <button
          type="submit"
          className="btn btn-info"
          disabled={!password.trim()}
        >
          <i className="fas fa-sign-in-alt mr-1"></i> Se connecter
        </button>
      </form>
    </>
  )
}

function StepMonitor({ status, deviceInfo, deviceInfoLoading, deviceInfoError, onRefreshDeviceInfo }) {
  return (
    <>
      <div className="alert alert-success mb-3">
        <i className="fas fa-check-circle mr-1"></i>
        Module IA configuré avec succès.
      </div>

      {status && (
        <div className="row mb-3">
          <div className="col-sm-4">
            <strong>IP :</strong> {status.ipAddress || "-"}
          </div>
          <div className="col-sm-4">
            <strong>Connexion :</strong>{" "}
            <span className={`badge badge-${status.connectionStatus === "connected" ? "success" : "secondary"}`}>
              {status.connectionStatus === "connected" ? "Connecté" : status.connectionStatus || "Inconnu"}
            </span>
          </div>
          <div className="col-sm-4">
            <strong>Activation :</strong>{" "}
            <span className="badge badge-success">Activé</span>
          </div>
        </div>
      )}

      {deviceInfoLoading && (
        <div className="text-center py-3">
          <div className="spinner-border text-info" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
          <p className="mt-2 text-muted">Interrogation du module IA en cours...</p>
        </div>
      )}

      {deviceInfoError && !deviceInfoLoading && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {deviceInfoError}
        </div>
      )}

      {deviceInfo && !deviceInfoLoading && (
        <>
          {deviceInfo.device && (
            <>
              <h6 className="text-muted text-uppercase mb-2">
                <i className="fas fa-microchip mr-1"></i> Identité du périphérique
              </h6>
              <table className="table table-sm table-bordered mb-3">
                <tbody>
                  <tr>
                    <th style={{ width: "35%" }}>Nom</th>
                    <td>{deviceInfo.device.deviceName || "-"}</td>
                  </tr>
                  <tr>
                    <th>Modèle</th>
                    <td>{deviceInfo.device.deviceModel || "-"}</td>
                  </tr>
                  <tr>
                    <th>N° de série</th>
                    <td>{deviceInfo.device.deviceSn || "-"}</td>
                  </tr>
                  <tr>
                    <th>Firmware</th>
                    <td>{deviceInfo.device.firmwareVersion || "-"}</td>
                  </tr>
                  <tr>
                    <th>Version algorithme</th>
                    <td>{deviceInfo.device.algorithmVersion || "-"}</td>
                  </tr>
                  <tr>
                    <th>Puce</th>
                    <td>{deviceInfo.device.chip || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {deviceInfo.runtime && (
            <>
              <h6 className="text-muted text-uppercase mb-2">
                <i className="fas fa-heartbeat mr-1"></i> État du système
              </h6>
              <table className="table table-sm table-bordered mb-3">
                <tbody>
                  <tr>
                    <th style={{ width: "35%" }}>CPU</th>
                    <td>{deviceInfo.runtime.cpuUsage != null ? `${deviceInfo.runtime.cpuUsage}%` : "-"}</td>
                  </tr>
                  <tr>
                    <th>Température CPU</th>
                    <td>{deviceInfo.runtime.cpuTemperature != null ? `${deviceInfo.runtime.cpuTemperature}°C` : "-"}</td>
                  </tr>
                  <tr>
                    <th>Mémoire</th>
                    <td>
                      {deviceInfo.runtime.memoryUsed != null && deviceInfo.runtime.memoryTotal != null
                        ? `${deviceInfo.runtime.memoryUsed} / ${deviceInfo.runtime.memoryTotal} Mo`
                        : "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {deviceInfo.intelligence && (
            <>
              <h6 className="text-muted text-uppercase mb-2">
                <i className="fas fa-brain mr-1"></i> Intelligence
              </h6>
              <div className="mb-2">
                <strong>Algorithmes supportés :</strong>{" "}
                {deviceInfo.intelligence.supportedAlgorithms?.length > 0 ? (
                  deviceInfo.intelligence.supportedAlgorithms.map((algo) => (
                    <span key={algo} className="badge badge-info mr-1">{algo}</span>
                  ))
                ) : (
                  <span className="text-muted">Aucun</span>
                )}
              </div>
              {deviceInfo.intelligence.activeMonitors?.length > 0 && (
                <div className="mb-2">
                  <strong>Moniteurs actifs :</strong>{" "}
                  {deviceInfo.intelligence.activeMonitors.map((m, i) => (
                    <span key={i} className="badge badge-success mr-1">{typeof m === 'string' ? m : (m.monitorId || m.name || '-')}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      <button
        className="btn btn-outline-info btn-sm mt-2"
        onClick={onRefreshDeviceInfo}
        disabled={deviceInfoLoading}
      >
        <i className="fas fa-sync-alt mr-1"></i> Rafraîchir
      </button>
    </>
  )
}

const CONFIG_OPERATIONS = [
  { key: "port_config", label: "Communication HTTP (port 80)" },
  { key: "system_time", label: "Date et fuseau horaire" },
  { key: "alarm_record_config", label: "Stockage des enregistrements" },
]

function StepConfiguration({ configuring, configResults, configError, onRetry }) {
  const resultMap = {}
  if (configResults) {
    for (const r of configResults) resultMap[r.operation] = r
  }

  const allSuccess = configResults?.length > 0 && configResults.every((r) => r.success)
  const hasFailure = configResults?.some((r) => !r.success)

  return (
    <>
      <p>Application automatique des paramètres au module IA.</p>

      <ul className="ai-config-list list-unstyled mb-3">
        {CONFIG_OPERATIONS.map(({ key, label }) => {
          const result = resultMap[key]
          let icon, badgeClass, badgeLabel

          if (configuring && !result) {
            icon = "fas fa-spinner fa-spin text-info"
            badgeClass = "badge-secondary"
            badgeLabel = "En cours..."
          } else if (result?.success) {
            icon = "fas fa-check-circle text-success"
            badgeClass = "badge-success"
            badgeLabel = "OK"
          } else if (result && !result.success) {
            icon = "fas fa-times-circle text-danger"
            badgeClass = "badge-danger"
            badgeLabel = "Échec"
          } else {
            icon = "fas fa-circle text-muted"
            badgeClass = "badge-secondary"
            badgeLabel = "En attente"
          }

          return (
            <li key={key} className="ai-config-item">
              <i className={`${icon} mr-2`}></i>
              <span className="ai-config-label">{label}</span>
              <span className={`badge ${badgeClass} ml-auto`}>{badgeLabel}</span>
              {result && !result.success && result.error && (
                <small className="ai-config-error text-danger d-block w-100 ml-4 mt-1">{result.error}</small>
              )}
            </li>
          )
        })}
      </ul>

      {configError && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {configError}
        </div>
      )}

      {allSuccess && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle mr-1"></i>
          Tous les paramètres ont été appliqués avec succès.
        </div>
      )}

      {hasFailure && !configuring && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle mr-1"></i>
          Certains paramètres n'ont pas pu être appliqués. Vous pouvez réessayer ou terminer l'assistant.
        </div>
      )}
    </>
  )
}
